import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarRange, ArrowUp, ArrowDown, Check, X, Plus, Trash2, Save, FileSpreadsheet, Printer, Repeat } from "lucide-react";
import { STATUS_META, WEEKDAYS, todayISO, addDays } from "./scheduleUtils";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function ScheduleTab() {
  const { can } = useAuth();
  const canManage = can("hr:schedule_manage");
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(addDays(todayISO(), 30));
  const [sched, setSched] = useState(null);
  const [rotations, setRotations] = useState([]);
  const [packers, setPackers] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [shiftGroups, setShiftGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [overrides, setOverrides] = useState([]);
  // rotation dialog
  const [rotOpen, setRotOpen] = useState(false);
  const [rotForm, setRotForm] = useState({ start_sunday: "", effective_date: todayISO(), order: [], active: true });
  // assignment form
  const [asg, setAsg] = useState({ employee_id: "", shift_group_id: "", effective_from: todayISO(), effective_to: todayISO() });
  // override form
  const [ovr, setOvr] = useState({ employee_id: "", date: todayISO() });
  // roster
  const now = new Date();
  const [rMonth, setRMonth] = useState(now.getMonth() + 1);
  const [rYear, setRYear] = useState(now.getFullYear());

  const loadRotations = () => api.get("/hr/schedule/sunday-rotations").then(r => { setRotations(r.data.rotations); setPackers(r.data.active_packing); }).catch(() => {});
  const loadSwaps = () => api.get("/hr/schedule/swaps").then(r => setSwaps(r.data)).catch(() => {});
  const loadAsg = () => api.get("/hr/schedule/shift-assignments").then(r => setAssignments(r.data)).catch(() => {});
  const loadOvr = () => api.get("/hr/schedule/overrides").then(r => setOverrides(r.data)).catch(() => {});
  useEffect(() => {
    loadRotations(); loadSwaps(); loadAsg(); loadOvr();
    api.get("/hr/shift-groups").then(r => setShiftGroups(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    try { const { data } = await api.get(`/hr/schedule?start=${start}&end=${end}`); setSched(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const finalize = async () => {
    if (!window.confirm("Finalize (save) daily schedule records for this range? Existing records for these dates are updated.")) return;
    try { const { data } = await api.post("/hr/schedule/generate", { start, end }); toast.success(`Finalized ${data.finalized_records} records`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setOff = async (employee_id, off_weekday) => {
    try { await api.post("/hr/schedule/off-day", { employee_id, off_weekday }); toast.success("Off day set"); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // rotation dialog helpers
  const openRot = () => { setRotForm({ start_sunday: "", effective_date: todayISO(), order: packers.map(p => p.employee_id), active: true }); setRotOpen(true); };
  const moveRot = (i, dir) => { const n = [...rotForm.order]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setRotForm({ ...rotForm, order: n }); };
  const saveRot = async () => {
    if (!rotForm.start_sunday) return toast.error("Pick a start Sunday");
    try { await api.post("/hr/schedule/sunday-rotations", rotForm); toast.success("Rotation config created"); setRotOpen(false); loadRotations(); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleRot = async (r) => {
    try { await api.patch(`/hr/schedule/sunday-rotations/${r.id}`, { active: !r.active }); loadRotations(); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addAsg = async () => {
    if (!asg.employee_id || !asg.shift_group_id) return toast.error("Pick employee and shift");
    try { await api.post("/hr/schedule/shift-assignments", asg); toast.success("Shift assigned"); loadAsg(); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAsg = async (id) => { try { await api.delete(`/hr/schedule/shift-assignments/${id}`); loadAsg(); if (sched) generate(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const addOvr = async () => {
    if (!ovr.employee_id || !ovr.date) return toast.error("Pick employee and date");
    try { await api.post("/hr/schedule/overrides", ovr); toast.success("Override added"); loadOvr(); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delOvr = async (id) => { try { await api.delete(`/hr/schedule/overrides/${id}`); loadOvr(); if (sched) generate(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const respondSwap = async (id, action) => {
    try { await api.post(`/hr/schedule/swaps/${id}/respond`, { action }); toast.success(`Swap ${action}ed`); loadSwaps(); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openRoster = () => window.open(`/hr/roster?year=${rYear}&month=${rMonth}`, "_blank");
  const exportRoster = async () => {
    try {
      const res = await api.get(`/hr/schedule/roster.xlsx?year=${rYear}&month=${rMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `packing_roster_${rYear}_${String(rMonth).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  const nameOf = (eid) => packers.find(p => p.employee_id === eid)?.full_name || eid;

  return (
    <div data-testid="hr-schedule-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
        <div><Label>Start</Label><Input type="date" data-testid="sched-start" value={start} onChange={e => setStart(e.target.value)} className="h-9" /></div>
        <div><Label>End</Label><Input type="date" data-testid="sched-end" value={end} onChange={e => setEnd(e.target.value)} className="h-9" /></div>
        <Button onClick={generate} data-testid="sched-generate" className="bg-sky-600 hover:bg-sky-700 h-9"><CalendarRange className="w-4 h-4 mr-1.5" />Generate</Button>
        {canManage && <Button onClick={finalize} variant="outline" data-testid="sched-finalize" className="h-9"><Save className="w-4 h-4 mr-1.5" />Finalize</Button>}
        <div className="ml-auto flex items-end gap-2">
          <div><Label>Roster Month</Label>
            <Select value={String(rMonth)} onValueChange={v => setRMonth(Number(v))}>
              <SelectTrigger className="h-9 w-28" data-testid="roster-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Year</Label><Input type="number" className="h-9 w-24" value={rYear} onChange={e => setRYear(Number(e.target.value))} data-testid="roster-year" /></div>
          <Button onClick={openRoster} data-testid="roster-open" className="bg-slate-900 h-9"><Printer className="w-4 h-4 mr-1.5" />Monthly Roster</Button>
          <Button onClick={exportRoster} variant="outline" data-testid="roster-excel" className="h-9"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Excel</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-2 text-xs">
        {Object.entries(STATUS_META).map(([k, m]) => <span key={k} className={`px-2 py-0.5 rounded border ${m.cls}`}>{m.label}</span>)}
      </div>
      {sched && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto mb-6" data-testid="schedule-grid">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50"><th className="p-2 text-left sticky left-0 bg-slate-50">Employee</th>
              {sched.days.map(d => <th key={d.date} className={`p-1 text-center ${d.is_sunday ? "bg-violet-50" : ""}`}>{d.date.slice(5)}<div className="text-[9px] uppercase text-slate-400">{d.weekday}</div></th>)}
            </tr></thead>
            <tbody>
              {(sched.days[0]?.entries || []).map(row => (
                <tr key={row.employee_id} data-testid={`sched-emp-${row.employee_id}`}>
                  <td className="p-2 font-mono font-medium sticky left-0 bg-white whitespace-nowrap">{row.employee_id}<div className="text-[10px] text-slate-400 font-sans">{row.full_name}</div></td>
                  {sched.days.map(d => {
                    const e = d.entries.find(x => x.employee_id === row.employee_id) || {};
                    const m = STATUS_META[e.status] || STATUS_META.working;
                    return <td key={d.date} className="p-0.5 text-center">
                      <div className={`rounded border text-[10px] py-0.5 ${m.cls}`} title={`${e.status}${e.shift_name ? " · " + e.shift_name : ""}${e.note ? " · " + e.note : ""}`}>{e.label || m.label}</div>
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Config cards */}
      <div className="grid grid-cols-3 gap-5">
        {/* Sunday rotation configs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold flex items-center gap-1.5"><Repeat className="w-4 h-4 text-violet-600" />Sunday Rotation</h3>
            {canManage && <Button size="sm" variant="outline" onClick={openRot} data-testid="rotation-new"><Plus className="w-3.5 h-3.5 mr-1" />New</Button>}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2" data-testid="rotation-list">
            {rotations.map(r => (
              <div key={r.id} className={`rounded border p-2 text-xs ${r.active ? "border-violet-200 bg-violet-50/40" : "border-slate-200 opacity-70"}`} data-testid={`rotation-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Start {r.start_sunday}</span>
                  {canManage && <Switch checked={r.active} onCheckedChange={() => toggleRot(r)} data-testid={`rotation-active-${r.id}`} />}
                </div>
                <div className="text-slate-500">Effective {r.effective_date}</div>
                <div className="text-slate-600 mt-0.5">{r.order.map((eid, i) => `${i + 1}.${eid}`).join("  ")}</div>
              </div>
            ))}
            {rotations.length === 0 && <div className="text-slate-400 text-xs text-center py-3">No rotation configured yet.</div>}
          </div>

          {/* Weekly off days */}
          {canManage && (
            <div className="mt-4">
              <h3 className="font-semibold mb-2">Weekly Off Days</h3>
              <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-1.5" data-testid="offday-admin-list">
                {packers.map(p => (
                  <div key={p.employee_id} className="flex items-center justify-between text-sm gap-2">
                    <span className="font-mono text-xs">{p.employee_id}</span>
                    <Select onValueChange={v => setOff(p.employee_id, v)}>
                      <SelectTrigger className="h-8 w-32" data-testid={`offday-${p.employee_id}`}><SelectValue placeholder="Set off day" /></SelectTrigger>
                      <SelectContent>{WEEKDAYS.map(w => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
                {packers.length === 0 && <div className="text-slate-400 text-xs text-center py-2">No active packing staff.</div>}
              </div>
            </div>
          )}
        </div>

        {/* Shift assignments */}
        <div>
          <h3 className="font-semibold mb-2">Temporary Shift Assignment</h3>
          {canManage && (
            <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2 mb-2">
              <Select value={asg.employee_id} onValueChange={v => setAsg({ ...asg, employee_id: v })}>
                <SelectTrigger className="h-8" data-testid="asg-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
                <SelectContent>{packers.map(p => <SelectItem key={p.employee_id} value={p.employee_id}>{p.employee_id} · {p.full_name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={asg.shift_group_id} onValueChange={v => setAsg({ ...asg, shift_group_id: v })}>
                <SelectTrigger className="h-8" data-testid="asg-shift"><SelectValue placeholder="Shift" /></SelectTrigger>
                <SelectContent>{shiftGroups.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input type="date" className="h-8" value={asg.effective_from} onChange={e => setAsg({ ...asg, effective_from: e.target.value })} data-testid="asg-from" />
                <Input type="date" className="h-8" value={asg.effective_to} onChange={e => setAsg({ ...asg, effective_to: e.target.value })} data-testid="asg-to" />
              </div>
              <Button size="sm" onClick={addAsg} data-testid="asg-add" className="w-full bg-slate-900">Assign Shift</Button>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-1" data-testid="asg-list">
            {assignments.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1" data-testid={`asg-${a.id}`}>
                <span><b className="font-mono">{a.employee_id}</b> {a.shift_name}<br /><span className="text-slate-500">{a.effective_from} → {a.effective_to}</span></span>
                {canManage && <button onClick={() => delAsg(a.id)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>}
              </div>
            ))}
            {assignments.length === 0 && <div className="text-slate-400 text-xs text-center py-2">No temporary assignments.</div>}
          </div>
        </div>

        {/* Overrides + swaps */}
        <div>
          <h3 className="font-semibold mb-2">Admin Override (Off)</h3>
          {canManage && (
            <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2 mb-2">
              <Select value={ovr.employee_id} onValueChange={v => setOvr({ ...ovr, employee_id: v })}>
                <SelectTrigger className="h-8" data-testid="ovr-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
                <SelectContent>{packers.map(p => <SelectItem key={p.employee_id} value={p.employee_id}>{p.employee_id} · {p.full_name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" className="h-8" value={ovr.date} onChange={e => setOvr({ ...ovr, date: e.target.value })} data-testid="ovr-date" />
              <Button size="sm" onClick={addOvr} data-testid="ovr-add" className="w-full bg-slate-900">Mark Off</Button>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-1 mb-4" data-testid="ovr-list">
            {overrides.map(o => (
              <div key={o.id} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1">
                <span className="font-mono">{o.employee_id} · {o.date}</span>
                {canManage && <button onClick={() => delOvr(o.id)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>}
              </div>
            ))}
            {overrides.length === 0 && <div className="text-slate-400 text-xs text-center py-2">No overrides.</div>}
          </div>

          <h3 className="font-semibold mb-2">Sunday Swaps</h3>
          <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-1.5" data-testid="swaps-list">
            {swaps.map(s => (
              <div key={s.id} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1.5" data-testid={`swap-${s.id}`}>
                <span>{s.sunday_date}<br /><span className="text-slate-500">{s.requester_employee_id} → {s.target_employee_id}</span></span>
                <span className="flex items-center gap-1">
                  <Badge variant="outline" className={s.status === "accepted" ? "text-emerald-700 border-emerald-300" : s.status === "rejected" ? "text-rose-700 border-rose-300" : "text-amber-700 border-amber-300"}>{s.status}</Badge>
                  {canManage && s.status === "pending" && <>
                    <button onClick={() => respondSwap(s.id, "accept")} data-testid={`swap-accept-${s.id}`}><Check className="w-4 h-4 text-emerald-600" /></button>
                    <button onClick={() => respondSwap(s.id, "reject")}><X className="w-4 h-4 text-rose-600" /></button>
                  </>}
                </span>
              </div>
            ))}
            {swaps.length === 0 && <div className="text-slate-400 text-xs text-center py-2">No swap requests.</div>}
          </div>
        </div>
      </div>

      {/* Rotation dialog */}
      <Dialog open={rotOpen} onOpenChange={setRotOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Sunday Rotation Config</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Rotation Start Sunday *</Label><Input type="date" data-testid="rot-start" value={rotForm.start_sunday} onChange={e => setRotForm({ ...rotForm, start_sunday: e.target.value })} /></div>
            <div><Label>Effective Date *</Label><Input type="date" data-testid="rot-effective" value={rotForm.effective_date} onChange={e => setRotForm({ ...rotForm, effective_date: e.target.value })} /></div>
          </div>
          <div>
            <Label>Rotation Order (first = first off on start Sunday)</Label>
            <div className="space-y-1 mt-1" data-testid="rot-order">
              {rotForm.order.map((eid, i) => (
                <div key={eid} className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded text-sm">
                  <span className="font-mono">{i + 1}. {eid} <span className="text-slate-400 text-xs">{nameOf(eid)}</span></span>
                  <span className="flex gap-0.5">
                    <button onClick={() => moveRot(i, -1)}><ArrowUp className="w-3.5 h-3.5 text-slate-400" /></button>
                    <button onClick={() => moveRot(i, 1)}><ArrowDown className="w-3.5 h-3.5 text-slate-400" /></button>
                  </span>
                </div>
              ))}
              {rotForm.order.length === 0 && <div className="text-slate-400 text-xs">No active packing staff.</div>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={rotForm.active} onCheckedChange={v => setRotForm({ ...rotForm, active: v })} data-testid="rot-active" />Active</label>
          <DialogFooter><Button variant="outline" onClick={() => setRotOpen(false)}>Cancel</Button><Button onClick={saveRot} data-testid="rot-save" className="bg-slate-900">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
